import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Plus, 
  Eye, 
  Edit2, 
  Trash2, 
  BarChart3,
  Globe,
  Lock,
  FileText,
  Copy
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API_URL = "";

const ReportsListPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dashboardToDelete, setDashboardToDelete] = useState(null);
  const [duplicating, setDuplicating] = useState(null);

  useEffect(() => {
    fetchDashboards();
  }, []);

  const fetchDashboards = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboards`, {
        withCredentials: true
      });
      setDashboards(response.data);
    } catch (e) {
      console.error('Error fetching dashboards:', e);
      toast.error('Failed to load dashboards');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublish = async (dashboardId, currentStatus) => {
    try {
      await axios.post(`${API_URL}/api/dashboards/${dashboardId}/publish`, {}, {
        withCredentials: true
      });
      setDashboards(dashboards.map(d => 
        d.id === dashboardId ? { ...d, is_published: !currentStatus } : d
      ));
      toast.success(`Dashboard ${!currentStatus ? 'published' : 'unpublished'}`);
    } catch (e) {
      toast.error('Failed to update dashboard');
    }
  };

  const handleDelete = async () => {
    if (!dashboardToDelete) return;
    try {
      await axios.delete(`${API_URL}/api/dashboards/${dashboardToDelete.id}`, {
        withCredentials: true
      });
      setDashboards(dashboards.filter(d => d.id !== dashboardToDelete.id));
      toast.success('Dashboard deleted');
    } catch (e) {
      toast.error('Failed to delete dashboard');
    } finally {
      setDeleteDialogOpen(false);
      setDashboardToDelete(null);
    }
  };

  const handleDuplicate = async (dashboardId) => {
    setDuplicating(dashboardId);
    try {
      await axios.post(`${API_URL}/api/dashboards/${dashboardId}/duplicate`, {}, {
        withCredentials: true
      });
      toast.success('Dashboard duplicated! Check drafts below.');
      fetchDashboards();
    } catch (e) {
      toast.error('Failed to duplicate dashboard');
    } finally {
      setDuplicating(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-blue-700 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const publishedDashboards = dashboards.filter(d => d.is_published);
  const draftDashboards = dashboards.filter(d => !d.is_published);

  return (
    <div className="p-6" data-testid="reports-list-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-950">
            {isAdmin ? 'Manage Dashboards' : 'Dashboards'}
          </h1>
          <p className="text-zinc-500 mt-1">
            {isAdmin ? 'Create and manage data dashboards for users' : 'View published data dashboards'}
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => navigate('/builder')}
            className="bg-blue-700 hover:bg-blue-800 text-white"
            data-testid="create-dashboard-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Dashboard
          </Button>
        )}
      </div>

      {/* Published Dashboards */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-zinc-950 mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-green-600" />
          Published Dashboards
        </h2>
        
        {publishedDashboards.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {publishedDashboards.map((dashboard) => (
              <Card 
                key={dashboard.id} 
                className="border-zinc-200 bg-white hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/reports/${dashboard.id}`)}
                data-testid={`dashboard-card-${dashboard.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <BarChart3 className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-zinc-950">{dashboard.name}</h3>
                        <p className="text-xs text-zinc-500 flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {dashboard.file_name}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {dashboard.description && (
                    <p className="text-sm text-zinc-600 mb-3 line-clamp-2">{dashboard.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between text-xs text-zinc-500">
                    <span>{dashboard.charts?.length || 0} charts • {dashboard.kpis?.length || 0} KPIs</span>
                    <span>{dashboard.created_at ? format(new Date(dashboard.created_at), 'MMM d, yyyy') : ''}</span>
                  </div>
                  
                  {isAdmin && (
                    <div className="flex gap-2 mt-4 pt-3 border-t border-zinc-100" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/reports/${dashboard.id}`)}
                        className="flex-1 border-zinc-200"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/builder/${dashboard.id}`)}
                        className="border-zinc-200 text-blue-600"
                        data-testid={`edit-dashboard-${dashboard.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDuplicate(dashboard.id)}
                        disabled={duplicating === dashboard.id}
                        className="border-zinc-200 text-indigo-600"
                        data-testid={`duplicate-dashboard-${dashboard.id}`}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTogglePublish(dashboard.id, dashboard.is_published)}
                        className="border-zinc-200 text-amber-600"
                      >
                        <Lock className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setDashboardToDelete(dashboard);
                          setDeleteDialogOpen(true);
                        }}
                        className="border-zinc-200 text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-zinc-200 bg-white">
            <CardContent className="p-8 text-center">
              <Globe className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
              <p className="text-zinc-500">No published dashboards yet.</p>
              {isAdmin && (
                <Button
                  onClick={() => navigate('/builder')}
                  className="mt-4 bg-blue-700 hover:bg-blue-800 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Dashboard
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Draft Dashboards (Admin Only) */}
      {isAdmin && draftDashboards.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-zinc-950 mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5 text-zinc-500" />
            Draft Dashboards
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {draftDashboards.map((dashboard) => (
              <Card 
                key={dashboard.id} 
                className="border-zinc-200 bg-zinc-50 hover:shadow-md transition-shadow"
                data-testid={`draft-dashboard-card-${dashboard.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-zinc-200 rounded-lg">
                        <BarChart3 className="w-5 h-5 text-zinc-500" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-zinc-950">{dashboard.name}</h3>
                        <p className="text-xs text-zinc-500 flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {dashboard.file_name}
                        </p>
                      </div>
                    </div>
                    <span className="px-2 py-1 text-xs bg-zinc-200 text-zinc-600 rounded">Draft</span>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-zinc-500 mb-4">
                    <span>{dashboard.charts?.length || 0} charts • {dashboard.kpis?.length || 0} KPIs</span>
                    <span>{dashboard.created_at ? format(new Date(dashboard.created_at), 'MMM d, yyyy') : ''}</span>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/builder/${dashboard.id}`)}
                      className="flex-1 border-zinc-200 text-blue-600"
                      data-testid={`edit-draft-${dashboard.id}`}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleTogglePublish(dashboard.id, dashboard.is_published)}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Globe className="w-4 h-4 mr-1" />
                      Publish
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDuplicate(dashboard.id)}
                      disabled={duplicating === dashboard.id}
                      className="border-zinc-200 text-indigo-600"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setDashboardToDelete(dashboard);
                        setDeleteDialogOpen(true);
                      }}
                      className="border-zinc-200 text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Dashboard</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{dashboardToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ReportsListPage;
